import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, TextField, Paper, Tab, Alert, Chip, Stack } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import CollapsibleContent from '../components/CollapsibleContent';
import { PageTitle } from '../components/Page/PageTitle';
import { useTranslation } from 'react-i18next';
import { ROUTES_NS } from './i18n';
import { SettingsAPI } from '../apis/Settings';
import useAuthService from '../hooks/auths/useAuthService';

// const appVersion = process.env.REACT_APP_VERSION;

interface IBargeInfo {
  name: string;
  id: string;
  devices: {
    id: string;
    brandModel: string;
    measurementType: number;
    connectionInfo: {
      ipAddress: '10.0.5.14';
      portNumber: 502;
      slaveId: 1;
      readIntervalInSeconds: 30;
    };
  }[];
  imo: string;
}

interface IEdgeCred {
  Username: string;
  HashedPassword: string;
  Salt: string;
  Attributes: {
    roles: string;
  };
}

const SettingsPage = () => {
  const { t } = useTranslation(ROUTES_NS);
  const { isAuthenticated } = useAuthService();
  const [value, setValue] = useState('1');
  const [pageData, setPageData] = useState<IBargeInfo>();
  const [staticFileVer, setStaticFileVer] = useState('');
  const [edgeCreds, setEdgeCred] = useState<IEdgeCred[]>([]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated()) {
        const data = await SettingsAPI.getBargeInformation();
        const data2 = await SettingsAPI.getStaticFilesInformation();
        const data3 = await SettingsAPI.getEdgeCredentials();
        setPageData(data);
        setStaticFileVer(data2.path);
        setEdgeCred(data3);
      }
    })();
  }, [isAuthenticated]);

  const credentials = useMemo(() => {
    return (
      <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
        {edgeCreds.map((cred) => (
          <Chip key={cred.HashedPassword} label={cred.Username} variant="outlined" />
        ))}
      </Stack>
    );
  }, [edgeCreds]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <PageTitle>{t('settings')}</PageTitle>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleChange} sx={{ p: 0 }}>
            <Tab label="General Information" value="1" />
            <Tab label="System Configuration" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ p: 0 }}>
          {/* Vessel Identification Section */}
          <Box>
            <Typography
              sx={{
                fontWeight: '500',
                fontSize: '14px',
                lineHeight: '28px',
                font: 'Noto Sans',
                margin: '0',
                mt: 2,
              }}
            >
              Vessel Identification
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Vessel name"
                  value={pageData?.name}
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Vessel IMO"
                  value={pageData?.imo}
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Vessel SB"
                  value=""
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Vessel MMSI"
                  value=""
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                />
              </Grid>
            </Grid>
          </Box>

          {/* MFM Registration Section */}
          {pageData?.devices.map((info, idx) => {
            return (
              <Box key={info.id} sx={{ mt: 3 }}>
                <CollapsibleContent header={`Mass Flow Meter ${idx + 1}`}>
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="MFM friendly name"
                        value=""
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Manufacturer"
                        value={info.brandModel}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Manufacturer software version"
                        value=""
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Measurement type"
                        value={info.measurementType}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                  </Grid>
                </CollapsibleContent>
              </Box>
            );
          })}
        </TabPanel>
        <TabPanel value="2" sx={{ p: 0 }}>
          <Alert severity="warning" sx={{ mt: 2 }}>
            These are ADMIN ONLY settings.
          </Alert>

          {/* Vessel SettingsModal Section */}
          <Box>
            <Typography
              sx={{
                fontWeight: '500',
                fontSize: '14px',
                lineHeight: '28px',
                font: 'Noto Sans',
                margin: '0',
                mt: 2,
              }}
            >
              Vessel Settings [ADMIN]
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Edge credentials"
                  InputProps={{
                    readOnly: true,
                    startAdornment: credentials,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Web app version"
                  value={staticFileVer}
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  helperText={`Current version: ${staticFileVer}`}
                />
              </Grid>
            </Grid>
          </Box>

          {/* MFM Registration Section */}
          {pageData?.devices.map((info, idx) => {
            return (
              <Box key={idx} sx={{ mt: 3 }}>
                <CollapsibleContent header={`Mass Flow Meter ${idx + 1} [ADMIN]`}>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Device polling frequency"
                        value={info.connectionInfo.readIntervalInSeconds}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Device IP address"
                        value={info.connectionInfo.ipAddress}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Device port number"
                        value={info.connectionInfo.portNumber}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Device slave ID"
                        value={info.connectionInfo.slaveId}
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                      />
                    </Grid>
                  </Grid>
                </CollapsibleContent>
              </Box>
            );
          })}
        </TabPanel>
      </TabContext>
    </Paper>
  );
};

export default SettingsPage;
